import type { MoneyString } from "../finance/money";

/**
 * The BRANCH finance board ("F1"): the figures `GET /finance/overview/home` and the existing finance endpoints do
 * not give, for one operational branch.
 *
 * Purpose-built like the home and church payloads: each part answers one fixed question (is each account
 * reconciled and since when, where did the money come from and go compared with the previous period, how much of
 * every 100 received the branch keeps, what each fund can still spend, what blocks a clean close). Cash, the
 * period's income and expenses, remittances and the weekly series stay on `GET /finance/overview/home`; budgets on
 * the budget endpoints (ADR-0011); pledges on `GET /finance/pledges`. Nothing here is a report kernel.
 *
 * Every money value is a canonical decimal string at `currency` scale (IBA-116).
 */

/** How many income and expense lines are listed; the rest is summed as "other". */
export const BRANCH_FINANCE_BOARD_BREAKDOWN_LIMIT = 4;

/** How many funds are listed; the rest is summed as "other". */
export const BRANCH_FINANCE_BOARD_FUND_LIMIT = 4;

/** How many local months the "kept per 100 received" series covers, ending with the month of the period end. */
export const BRANCH_FINANCE_BOARD_MARGIN_MONTHS = 6;

/**
 * An account last reconciled longer ago than this (by its statement date) is overdue. A month and a few days,
 * so a monthly statement reconciled a little late is not flagged.
 */
export const BRANCH_FINANCE_BOARD_RECONCILIATION_OVERDUE_DAYS = 35;

/** Where one account stands with reconciliation, from its approved sessions only (IBA-161). */
export enum BranchAccountReconciliationState {
  /** Approved within {@link BRANCH_FINANCE_BOARD_RECONCILIATION_OVERDUE_DAYS} days. */
  Reconciled = "RECONCILED",
  /** Approved once, but longer ago than that. */
  Overdue = "OVERDUE",
  /** Never approved. */
  Never = "NEVER"
}

export interface BranchFinanceBoardAccount {
  accountId: string;
  name: string;
  /** Current balance (not period-bound), the same projection the accounts list serves. */
  balance: MoneyString;
  /** Statement date of the latest APPROVED reconciliation, or null. */
  lastReconciledAt: string | null;
  /** Whole days from `lastReconciledAt` to now, or null. */
  daysSinceReconciled: number | null;
  reconciliation: BranchAccountReconciliationState;
  /** A session is being prepared or waits for review. */
  hasOpenSession: boolean;
}

export interface BranchFinanceBoardBreakdownRow {
  accountingAccountId: string;
  name: string;
  translationKey: string | null;
  amount: MoneyString;
  /** The same line over the previous comparable period. */
  previousAmount: MoneyString;
  /** Share of the period total, in basis points. */
  shareBp: number;
  /** Share of the previous period's total, in basis points (0 when that total is zero). */
  previousShareBp: number;
}

/** The line whose amount moved most against the previous period, by relative change. */
export interface BranchFinanceBoardChange {
  accountingAccountId: string;
  name: string;
  translationKey: string | null;
  /** Signed relative change in basis points (+4000 = +40 %). */
  changeBp: number;
}

export interface BranchFinanceBoardBreakdown {
  total: MoneyString;
  previousTotal: MoneyString;
  rows: BranchFinanceBoardBreakdownRow[];
  /** Everything after the listed rows. */
  other: MoneyString;
  /**
   * Null when there is nothing honest to compare: no previous amounts, or no listed line that existed in both
   * periods. A line new this period has no relative change and is never chosen.
   */
  biggestChange: BranchFinanceBoardChange | null;
}

export interface BranchFinanceBoardMarginMonth {
  /** `YYYY-MM` in the branch calendar. */
  month: string;
  /** Of every 100 received that month, what was not spent (may be negative). Null when nothing was received. */
  keptPer100: number | null;
}

/**
 * Ratios of the branch's own income. Disclosed with the figures only: a reader the absolutes are withheld from
 * reads the operating margin of `GET /finance/overview/health`, which holds the ADR-0024 §6 read exemption.
 */
export interface BranchFinanceBoardMargin {
  /** Of every 100 received over the period, what was spent. Null when nothing was received. */
  spentPer100: number | null;
  /** `100 - spentPer100` (negative when the branch spent more than it received). */
  keptPer100: number | null;
  months: BranchFinanceBoardMarginMonth[];
}

export interface BranchFinanceBoardFund {
  fundId: string;
  name: string;
  /** The linked income account's translation key for a SYSTEM_LINKED fund, else null. */
  translationKey: string | null;
  /** What the fund may still commit: its balance minus what pending workflows hold. */
  available: MoneyString;
  /** Share of the positive available total, in basis points. */
  shareBp: number;
}

export interface BranchFinanceBoardFunds {
  /** Sum of every active fund's available amount. */
  total: MoneyString;
  fundCount: number;
  rows: BranchFinanceBoardFund[];
  other: MoneyString;
  otherShareBp: number;
}

export interface BranchFinanceBoardTransferToAccept {
  sourceBranchName: string;
  sourceIsParish: boolean;
  amount: MoneyString;
}

/** What blocks a clean close, as counts. */
export interface BranchFinanceBoardAttention {
  /** Live income and expense entries with no issued receipt and no payment reference (the investigation count). */
  missingEvidence: number;
  /** The same, recorded within the period. */
  missingEvidenceInPeriod: number;
  /** Accounts never reconciled or overdue. */
  accountsToReconcile: number;
  /** Transfers sent TO this branch that wait for its decision. */
  transfersToAccept: number;
  /** The oldest of them, or null. */
  firstTransferToAccept: BranchFinanceBoardTransferToAccept | null;
}

export interface BranchFinanceBoardFigures {
  accounts: BranchFinanceBoardAccount[];
  income: BranchFinanceBoardBreakdown;
  expenses: BranchFinanceBoardBreakdown;
  margin: BranchFinanceBoardMargin;
  funds: BranchFinanceBoardFunds;
  attention: BranchFinanceBoardAttention;
}

export interface BranchFinanceBoardOverview {
  workspaceId: string;
  branchId: string;
  /** The branch's accounting currency: the scale of every money string. */
  currency: string;
  dateRange: { from: string | null; to: string | null };
  /** The period of equal length just before `dateRange`, which the `previous*` figures cover. */
  previousDateRange: { from: string | null; to: string | null };
  /**
   * Whether this reader may see the branch's absolute figures (ADR-0024). False under BRANCH_AUTONOMY for a
   * reader outside the branch: `figures` is then null and none were read.
   */
  disclosed: boolean;
  figures: BranchFinanceBoardFigures | null;
}
